import { useRef } from "react";
import { Link } from "react-router-dom";
import { agentBodySrc, agentOfficeSrc, personaByAgentId } from "@/components/agents/agentPersonas";
import { Skeleton } from "@/components/ui/Skeleton";
import { HOME_AGENTS, type HomeAgent } from "@/lib/homeAgents";
import { cn } from "@shared/lib/utils";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

type AssistantBodyPanelProps = {
  agent: HomeAgent;
  /** Distinct finishes collected for this agent. */
  owned: number;
  /** Catalog size for this agent (finish tiers). */
  total: number;
  loading: boolean;
  authenticated: boolean;
  onSignIn: () => void;
  className?: string;
};

/**
 * Collection showcase — left zone. The selected assistant's full-body art on a
 * flat stage (AgentStage presentation grammar: accent bloom behind, bottom
 * anchor on lg+, 300ms crossfade between the six stacked webps), with the
 * identity block + per-agent collection progress top-left.
 *
 * <lg it collapses to the compact h-40 banner: art peeks from the right behind
 * a horizontal scrim, identity/progress sit left.
 */
export function AssistantBodyPanel({
  agent,
  owned,
  total,
  loading,
  authenticated,
  onSignIn,
  className,
}: AssistantBodyPanelProps) {
  const reducedMotion = usePrefersReducedMotion();
  const persona = personaByAgentId(agent.agentId);
  const ratio = total > 0 ? Math.min(1, owned / total) : 0;

  // 첫 로드에 6에이전트 × (전신+오피스) 12장(~1.4MB)이 전부 내려오던 문제 —
  // 한 번이라도 선택된 에이전트의 레이어만 마운트. 첫 선택은 즉시 표시,
  // 이후 재방문부터 기존 300ms 크로스페이드가 그대로 동작한다.
  const seenRef = useRef<Set<string>>(new Set());
  seenRef.current.add(agent.agentId);
  const layers = HOME_AGENTS.filter((entry) => seenRef.current.has(entry.agentId));

  return (
    <div className={cn("relative isolate h-40 overflow-hidden lg:h-full lg:min-h-0", className)}>
      {/* Component-scoped keyframes — styles/*.css stays untouched. */}
      <style>{`@keyframes ty-cs-fade { from { opacity: 0; } to { opacity: 1; } }`}</style>

      {/* 캐릭터 오피스 배경 — 가장 깊은 레이어, 바디와 함께 크로스페이드. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-20">
        {layers.map((entry) => (
          <img
            key={entry.agentId}
            src={agentOfficeSrc(entry.name)}
            alt=""
            loading="lazy"
            className={cn(
              "absolute inset-0 h-full w-full object-cover transition-opacity duration-300 ease-(--ty-ease) motion-reduce:transition-none",
              entry.agentId === agent.agentId ? "opacity-100" : "opacity-0",
            )}
          />
        ))}
        {/* 가독성 딤 — Tailwind var-단축엔 /알파 수식이 안 붙으므로 인라인 color-mix 사용 */}
        <div
          className="absolute inset-0"
          style={{ background: "color-mix(in oklab, var(--ty-bg-2) 78%, transparent)" }}
        />
      </div>

      {/* Backdrop — soft top light + accent bloom, both behind the art. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_50%_at_50%_-12%,rgba(255,255,255,0.05),transparent_65%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(ellipse 60% 44% at 50% 76%, color-mix(in oklab, var(--ty-accent) 10%, transparent), transparent 70%)",
        }}
      />

      {/* Body layers — per-agent 300ms crossfade. */}
      <div aria-hidden className="absolute inset-0">
        {layers.map((entry) => {
          const active = entry.agentId === agent.agentId;
          return (
            <img
              key={entry.agentId}
              src={agentBodySrc(entry.name)}
              alt=""
              loading={active ? "eager" : "lazy"}
              className={cn(
                // 전신이 항상 보이도록 패널 박스 전체에 object-contain (크롭 금지).
                // <lg: 우하단 정렬 뒤 텍스트와 공존, lg+: 상단 아이덴티티 아래 중앙-하단.
                "absolute inset-0 h-full w-full object-contain object-right-bottom transition-opacity duration-300 ease-(--ty-ease) motion-reduce:transition-none lg:px-6 lg:pt-14 lg:object-bottom",
                active ? "opacity-100" : "opacity-0",
              )}
            />
          );
        })}
      </div>

      {/* Scrims — <lg horizontal left→right; lg+ vertical top→down behind the copy. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-(--ty-bg-2) from-30% via-(--ty-bg-2)/55 to-transparent lg:hidden"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 hidden h-48 bg-gradient-to-b from-(--ty-bg-2)/90 via-(--ty-bg-2)/40 to-transparent lg:block"
      />

      {/* Identity + progress — remounted per agent so it fades with the art. */}
      <div
        key={agent.agentId}
        className="absolute inset-y-0 left-4 right-4 flex max-w-[26rem] flex-col justify-center lg:inset-y-auto lg:left-6 lg:right-6 lg:top-6 lg:justify-start"
        style={reducedMotion ? undefined : { animation: "ty-cs-fade 0.3s var(--ty-ease) both" }}
      >
        <p className="ty-eyebrow text-[10px]">
          <span className="text-accent">{persona?.shortLabel ?? "AGENT"}</span>
          <span aria-hidden> — </span>
          {persona?.role ?? ""}
        </p>
        <h3 className="mt-1 font-display text-2xl font-bold text-ink lg:mt-1.5 lg:text-3xl">
          {agent.name}
        </h3>

        {/* Per-agent progress — mono readout + slim accent bar. */}
        <div className="mt-3 lg:mt-4">
          {loading ? (
            <>
              <Skeleton className="h-3 w-28" />
              <Skeleton className="mt-2 h-1 w-full max-w-[11rem]" />
            </>
          ) : (
            <>
              <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-dim">
                <span className="tabular-nums text-ink">{owned}</span>{" "}
                <span aria-hidden>/</span> <span className="tabular-nums">{total}</span> collected
              </p>
              <div
                role="progressbar"
                aria-label={`${agent.name} collection progress`}
                aria-valuemin={0}
                aria-valuemax={total}
                aria-valuenow={owned}
                className="mt-2 h-0.5 w-full max-w-[11rem] bg-white/10"
              >
                <div
                  className="h-full w-full origin-left bg-accent transition-transform duration-300 ease-(--ty-ease) motion-reduce:transition-none"
                  style={{ transform: `scaleX(${ratio})` }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Bottom-left ghost CTA (lg+; the mobile banner stays copy-only). */}
      <div className="absolute bottom-5 left-6 hidden lg:block">
        {authenticated ? (
          <Link
            to="/profile/collection"
            className="inline-flex min-h-11 items-center font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-ink-dim transition-colors duration-200 hover:text-ink"
          >
            View all <span aria-hidden className="text-accent">→</span>
          </Link>
        ) : (
          <button
            type="button"
            onClick={onSignIn}
            className="inline-flex min-h-11 items-center font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-accent transition-colors duration-200 hover:text-accent-2"
          >
            Sign in with Telegram <span aria-hidden>→</span>
          </button>
        )}
      </div>
    </div>
  );
}
